import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { checkWebAddressCountries } from './company.repo';

/**
 * Server-side redirect check for marketplace companies.
 * Returns a Next.js redirect object if the company should redirect to the marketplace,
 * or null if no redirect is needed.
 *
 * The destination preserves the original path (minus the country prefix already
 * included in redirect_url) and the query string, so deep links survive the redirect.
 *
 * Example:
 *   In:  https://foo.site.agendaprodev.com/cl/sign_consents/my_consent?code=abc
 *   Out: https://www.agendaprodev.com/site/cl/foo/sign_consents/my_consent?code=abc
 */
export const getMarketplaceRedirect = async (
  ctx: GetServerSidePropsContext,
): Promise<{ redirect: { permanent: boolean; destination: string } } | null> => {
  const companySlug = ctx.req.headers.host?.split('.')[0];

  if (!companySlug) {
    return null;
  }

  try {
    const { countries, redirect } = await checkWebAddressCountries(companySlug);

    if (!redirect?.needsRedirect || !redirect?.redirectUrl) {
      return null;
    }

    // The backend computes a single redirect for ONE company sharing this
    // web_address (company_countries.first), ignoring the country in the URL.
    // A web_address is unique only per country, so the same slug (e.g. "focus")
    // can belong to different companies in different countries. Only honor the
    // redirect when the country in the URL path matches the country the redirect
    // was built for; otherwise a marketplace company in country A would hijack
    // the legacy site of country B. See company 417785 (AR) vs 540089 (CL).
    const requestedCountry = (ctx.req.url ?? '/').split('?')[0].split('/').filter(Boolean)[0];
    const redirectCountry = redirect.redirectUrl.match(/\/site\/([^/]+)\//)?.[1];

    if (requestedCountry && redirectCountry && requestedCountry !== redirectCountry) {
      return null;
    }

    // At the root (no country in path) with several countries sharing the
    // web_address, let the country picker resolve it instead of guessing one.
    if (!requestedCountry && (countries?.length ?? 0) > 1) {
      return null;
    }

    const base = redirect.redirectUrl.replace(/\/$/, '');
    // E-commerce routes start with /[countryCode]/..., and the e-commerce uses
    // /sucursal/<id> while the marketplace uses /<id> directly. We strip both
    // segments so the deep link resolves to the correct marketplace URL.
    const rawPath = (ctx.req.url ?? '').replace(/^\/[^/?]+(\/sucursal)?/, '');
    const pathWithoutCountry = rawPath === '/' ? '' : rawPath;

    return {
      redirect: {
        permanent: false, // 302: site_type can change if the company upgrades/downgrades plan
        destination: `${base}${pathWithoutCountry}`,
      },
    };
  } catch {
    return null;
  }
};

/**
 * Wraps a getServerSideProps handler so the marketplace redirect check runs first.
 * Centralizes the redirect-before-fetch pattern shared by every entry page.
 */
export const withMarketplaceRedirect =
  <P extends { [key: string]: any }>(handler: GetServerSideProps<P>): GetServerSideProps<P> =>
  async (ctx) => {
    const marketplaceRedirect = await getMarketplaceRedirect(ctx);

    if (marketplaceRedirect) {
      return marketplaceRedirect;
    }
    return handler(ctx);
  };
